import { api } from "./api.js";

/**
 * Registratie van de service worker en het aan- en afmelden voor meldingen.
 *
 * Over iOS: webpush werkt daar pas vanaf 16.4 én uitsluitend nadat de gebruiker
 * de app aan zijn beginscherm heeft toegevoegd. Vanuit een Safari-tabblad is
 * `Notification` simpelweg niet aanwezig. Dat is geen fout in onze code en de
 * UI moet dat ook zo uitleggen, anders gaat iemand eindeloos op een knop
 * drukken die niets doet.
 */

export type PushState =
  | "unsupported"      // browser kan het niet
  | "ios-needs-install" // iOS, maar de app staat nog niet op het beginscherm
  | "denied"           // gebruiker heeft geweigerd; alleen via browserinstellingen terug
  | "disabled"         // kan wel, staat uit
  | "enabled";

const VAPID_CACHE_KEY = "rw_vapid";

/**
 * base64url → ArrayBuffer, zoals PushManager de sleutel wil hebben.
 *
 * Bewust een ArrayBuffer en geen Uint8Array: sinds TypeScript 5.7 is
 * `Uint8Array<ArrayBufferLike>` niet meer toewijsbaar aan `BufferSource`, omdat
 * de view ook op een SharedArrayBuffer kan liggen. De onderliggende buffer
 * teruggeven omzeilt dat zonder een cast.
 */
function decodeKey(base64: string): ArrayBuffer {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const raw = atob(padded);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buffer;
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS gebruikt een eigen, niet-standaard vlag.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch {
    // Zonder service worker werkt de webmail gewoon; alleen offline starten en
    // meldingen vallen weg.
    return null;
  }
}

export async function pushState(): Promise<PushState> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    // Op iOS ontbreekt Notification zolang de app in een tabblad draait.
    return isIos() && !isStandalone() ? "ios-needs-install" : "unsupported";
  }
  if (Notification.permission === "denied") return "denied";

  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? "enabled" : "disabled";
}

export async function enablePush(): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "disabled";

  const reg = (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
  if (!reg) return "unsupported";

  // De publieke sleutel verandert vrijwel nooit; hem onthouden scheelt een
  // verzoek bij elke start.
  let publicKey = sessionStorage.getItem(VAPID_CACHE_KEY);
  if (publicKey === null) {
    const config = await api.pushConfig();
    if (!config.enabled || config.publicKey === null) return "unsupported";
    publicKey = config.publicKey;
    sessionStorage.setItem(VAPID_CACHE_KEY, publicKey);
  }

  const existing = await reg.pushManager.getSubscription();
  const subscription =
    existing ??
    (await reg.pushManager.subscribe({
      // Verplicht true: de browser eist dat elke push een zichtbare melding
      // oplevert. Stille pushes zijn niet toegestaan, en terecht.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(publicKey),
    }));

  await api.pushSubscribe(subscription.toJSON() as PushSubscriptionJSON);
  return "enabled";
}

/**
 * Houdt de server gelijk met dit apparaat: staat er een pushabonnement, dan
 * geldt het voor precies de mailboxen die hier nu aangemeld zijn. Aanroepen na
 * inloggen en na het toevoegen of afmelden van een mailbox — anders krijgt een
 * net toegevoegd account geen meldingen, of een afgemeld account nog wel.
 */
export async function syncPush(): Promise<void> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) await api.pushSubscribe(sub.toJSON() as PushSubscriptionJSON);
  } catch {
    // Meldingen zijn een extraatje; een mislukte synchronisatie mag niets blokkeren.
  }
}

export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  }
  return "disabled";
}

export { isIos, isStandalone };
